/**
 * Kleinpaket „Service Worker: Hinweis ‚Neue Version'": Anlass war ein offener Tab nach
 * einem Deploy, der nur noch weiße Seite bzw. Quelltext zeigte. sw.ts übernimmt neue
 * Fassungen sofort (skipWaiting() beim install, clients.claim() beim activate) — die
 * schon offene Seite bekommt davon aber nichts mit, bis sie selbst wieder etwas vom Netz
 * anfordert, und reißt dabei mitten in einem laufenden Zustand den Boden unter sich weg.
 * "controllerchange" auf navigator.serviceWorker ist das Ereignis, das genau DAS anzeigt:
 * ein anderer Worker hat die Seite übernommen.
 *
 * Reine Entscheidungslogik ausgelagert in beobachteNeueVersion(), damit sie sich ohne
 * echten ServiceWorkerContainer testen lässt (siehe registerSw.test.ts).
 */
type ServiceWorkerContainerAusschnitt = {
  controller: unknown;
  addEventListener: (type: "controllerchange", listener: () => void) => void;
};

/**
 * Meldet über `melde()` genau einmal, dass eine neue Version übernommen hat — und nur
 * dann, wenn VOR dem ersten "controllerchange" schon ein controller gesetzt war. War er
 * null, ist es der allererste Besuch (kein Wechsel von einer Fassung zu einer anderen,
 * also nichts zu melden).
 */
export function beobachteNeueVersion(
  sw: ServiceWorkerContainerAusschnitt,
  melde: () => void,
): void {
  const hatteSchonEinenController = sw.controller !== null;
  let gemeldet = false;
  sw.addEventListener("controllerchange", () => {
    if (!hatteSchonEinenController || gemeldet) return;
    gemeldet = true;
    melde();
  });
}

/**
 * Meldet den Service Worker nur in der ausgelieferten Fassung an (import.meta.env.PROD).
 * In der Entwicklung bliebe ein einmal installierter Vorrat über Vite-Neustarts hinweg
 * bestehen und würde alte Stände ausliefern — genau die Störung, die vermieden werden soll.
 *
 * "/sw.js" liegt an der Wurzel (siehe vite.config.ts, zweiter Rollup-Einstiegspunkt),
 * damit sein scope die ganze Anwendung ("/") umfasst, nicht nur ein Unterverzeichnis.
 */
export function registerSw(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
    beobachteNeueVersion(navigator.serviceWorker, () =>
      window.dispatchEvent(new CustomEvent("ki:neue-version")));
  });
}
